// screens/marketplace/CourseEnrollmentCallback.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Send,
  Copy,
  Check,
  ChevronLeft,
} from "lucide-react";
import { useAuth } from "../../providers/AuthProvider";
import { useTheme } from "../../hooks/useTheme";
import { useToast } from "../../providers/ToastProvider";
import { getEnrollment, isPaid } from "./coursesApi";

/**
 * Landing page for the Paystack return URL.
 *
 * Paystack redirects back with `?reference=...&trxref=...`. We poll the
 * enrollment until its payment_status flips to COMPLETED, then reveal
 * the Telegram link.
 *
 * The reference is opaque — we resolve the enrollment via the current
 * user's enrollments list and match on reference. Small datasets, fine.
 */
export default function CourseEnrollmentCallback() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { authFetch, isAuthenticated } = useAuth();
  const { colors } = useTheme();
  const { showToast } = useToast();

  const [enrollment, setEnrollment] = useState(null);
  const [status, setStatus] = useState("verifying"); // verifying | paid | failed | not_found
  const [copied, setCopied] = useState(false);
  const pollTimer = useRef(null);
  const attemptsRef = useRef(0);

  const reference =
    params.get("reference") || params.get("trxref") || "";

  const verify = useCallback(async () => {
    if (!isAuthenticated) {
      navigate("/login?next=/courses");
      return;
    }
    attemptsRef.current += 1;

    try {
      // Resolve reference → enrollment by scanning the user's enrollments.
      // If your backend adds a by-reference endpoint later, swap this.
      const res = await authFetch("/marketplace/courses/enrollments/");
      const data = await res.json();
      if (!res.ok) throw new Error("Could not load enrollments");

      const list = data.data || [];
      // Prefer matching by reference if the API returns it; otherwise
      // pick the newest PENDING/PAID enrollment from the last few minutes.
      const match =
        list.find((e) => e.reference && e.reference === reference) ||
        list
          .filter((e) => {
            const t = new Date(e.created_at || 0).getTime();
            return Date.now() - t < 10 * 60 * 1000;
          })
          .sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
          )[0];

      if (!match) {
        if (attemptsRef.current < 6) {
          pollTimer.current = setTimeout(verify, 1500);
        } else {
          setStatus("not_found");
        }
        return;
      }

      setEnrollment(match);

      if (isPaid(match)) {
        setStatus("paid");
        return;
      }
      if (match.status === "FAILED") {
        setStatus("failed");
        return;
      }

      // Still PENDING — keep polling
      if (attemptsRef.current < 10) {
        pollTimer.current = setTimeout(verify, 1500);
      } else {
        setStatus("failed");
      }
    } catch (err) {
      showToast(err.message, "error");
      setStatus("failed");
    }
  }, [authFetch, isAuthenticated, navigate, reference, showToast]);

  useEffect(() => {
    verify();
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyLink = async () => {
    if (!enrollment?.telegram_link) return;
    try {
      await navigator.clipboard.writeText(enrollment.telegram_link);
      setCopied(true);
      showToast("Telegram link copied", "success");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast("Could not copy link", "error");
    }
  };

  /* ── Verifying ──────────────────────────────────── */
  if (status === "verifying") {
    return (
      <div style={styles.pageWrap}>
        <div style={styles.center(colors)}>
          <Loader2
            size={32}
            color={colors.btnBg}
            style={{ animation: "spin 0.9s linear infinite" }}
          />
          <div style={styles.h2(colors)}>Confirming your payment…</div>
          <p style={styles.sub(colors)}>
            This usually takes a few seconds. Don't close this tab.
          </p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  /* ── Failed ─────────────────────────────────────── */
  if (status === "failed" || status === "not_found") {
    return (
      <div style={styles.pageWrap}>
        <button
          type="button"
          onClick={() => navigate("/courses")}
          style={styles.backBtn(colors)}
        >
          <ChevronLeft size={16} /> All courses
        </button>
        <div style={styles.center(colors)}>
          <div style={styles.statusIcon(colors, "error")}>
            <XCircle size={28} />
          </div>
          <div style={styles.h2(colors)}>
            {status === "not_found"
              ? "We couldn't find your enrollment"
              : "Payment not confirmed yet"}
          </div>
          <p style={styles.sub(colors)}>
            {status === "not_found"
              ? "If you completed payment, check your enrollments page in a minute."
              : "We didn't receive a confirmation in time. If money left your account, check the enrollments page."}
          </p>
          <div style={styles.actionsRow}>
            <button
              type="button"
              onClick={() => navigate("/courses/enrollments")}
              style={styles.primaryBtn(colors)}
            >
              My enrollments
            </button>
            <button
              type="button"
              onClick={() => navigate("/courses")}
              style={styles.secondaryBtn(colors)}
            >
              Browse courses
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Paid ───────────────────────────────────────── */
  return (
    <div style={styles.pageWrap}>
      <button
        type="button"
        onClick={() => navigate("/courses/enrollments")}
        style={styles.backBtn(colors)}
      >
        <ChevronLeft size={16} /> My enrollments
      </button>

      <div style={styles.successWrap(colors)}>
        <div style={styles.statusIcon(colors, "success")}>
          <CheckCircle2 size={32} />
        </div>
        <h1 style={styles.h1(colors)}>You're in</h1>
        <p style={styles.sub(colors)}>
          Payment confirmed for <strong>{enrollment.course_title}</strong>.
          Join the Telegram group with the link below and mention your
          username so we can accept your request.
        </p>

        <div style={styles.linkBox(colors)}>
          <div style={styles.linkHeader(colors)}>
            <Send size={14} />
            Telegram invite
          </div>
          <div style={styles.linkRow(colors)}>
            <span
              style={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: 13,
                color: colors.text,
                fontWeight: 600,
              }}
            >
              {enrollment.telegram_link}
            </span>
            <button
              type="button"
              onClick={copyLink}
              style={styles.copyBtn(colors, copied)}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <a
            href={enrollment.telegram_link}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.openLink(colors)}
          >
            Open in Telegram
          </a>
        </div>

        <div style={styles.metaBox(colors)}>
          <div style={styles.metaRow}>
            <span>Contact</span>
            <span>
              {enrollment.contact_name} · {enrollment.contact_email}
            </span>
          </div>
          <div style={styles.metaRow}>
            <span>Telegram</span>
            <span>{enrollment.telegram_username}</span>
          </div>
          <div style={styles.metaRow}>
            <span>Amount paid</span>
            <span>GH₵{Number(enrollment.amount).toFixed(2)}</span>
          </div>
        </div>

        <div style={styles.actionsRow}>
          <button
            type="button"
            onClick={() => navigate("/courses/enrollments")}
            style={styles.secondaryBtn(colors)}
          >
            My enrollments
          </button>
          <button
            type="button"
            onClick={() => navigate("/courses")}
            style={styles.primaryBtn(colors)}
          >
            Browse more courses
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Styles
   ══════════════════════════════════════════════════════════ */

const styles = {
  pageWrap: {
    maxWidth: 720,
    margin: "0 auto",
    padding: "20px 16px 64px",
  },
  backBtn: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: "transparent",
    border: "none",
    color: c.secondaryText,
    cursor: "pointer",
    padding: "6px 0",
    marginBottom: 14,
    fontSize: 13,
    fontWeight: 600,
  }),

  center: (c) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    minHeight: 380,
    gap: 12,
    padding: 24,
  }),

  statusIcon: (c, tone) => ({
    width: 72,
    height: 72,
    borderRadius: "50%",
    background: tone === "success" ? c.successBg : c.errorBg,
    color: tone === "success" ? c.success : c.error,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
  }),

  h1: (c) => ({
    margin: 0,
    fontSize: 26,
    fontWeight: 800,
    color: c.heading,
    letterSpacing: "-0.02em",
  }),
  h2: (c) => ({
    fontSize: 16,
    fontWeight: 700,
    color: c.heading,
    marginTop: 4,
  }),
  sub: (c) => ({
    margin: "4px 0 0",
    fontSize: 14,
    color: c.secondaryText,
    lineHeight: 1.6,
    maxWidth: 460,
  }),

  successWrap: (c) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    background: c.raisedBg,
    border: `1px solid ${c.border}`,
    borderRadius: 20,
    padding: "36px 24px",
    gap: 16,
  }),

  linkBox: (c) => ({
    width: "100%",
    textAlign: "left",
    background: c.subtleBg || c.hoverBg,
    border: `1px solid ${c.border}`,
    borderRadius: 14,
    padding: 14,
    marginTop: 12,
  }),
  linkHeader: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: c.secondaryText,
    marginBottom: 8,
  }),
  linkRow: (c) => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 10px",
    background: c.raisedBg,
    border: `1px solid ${c.border}`,
    borderRadius: 10,
  }),
  copyBtn: (c, active) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "6px 10px",
    borderRadius: 8,
    border: "none",
    background: active ? c.successBg : c.btnBg,
    color: active ? c.success : c.btnText,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    flexShrink: 0,
  }),
  openLink: (c) => ({
    display: "block",
    marginTop: 10,
    padding: "10px 14px",
    borderRadius: 10,
    background: c.btnBg,
    color: c.btnText,
    fontWeight: 700,
    fontSize: 13.5,
    textAlign: "center",
    textDecoration: "none",
  }),

  metaBox: (c) => ({
    width: "100%",
    textAlign: "left",
    background: c.subtleBg || c.hoverBg,
    border: `1px solid ${c.border}`,
    borderRadius: 12,
    padding: 14,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  }),
  metaRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    fontSize: 13,
  },

  actionsRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    justifyContent: "center",
    marginTop: 12,
  },
  primaryBtn: (c) => ({
    padding: "11px 20px",
    borderRadius: 10,
    border: "none",
    background: c.btnBg,
    color: c.btnText,
    fontWeight: 700,
    fontSize: 13.5,
    cursor: "pointer",
  }),
  secondaryBtn: (c) => ({
    padding: "11px 20px",
    borderRadius: 10,
    border: `1px solid ${c.border}`,
    background: "transparent",
    color: c.text,
    fontWeight: 600,
    fontSize: 13.5,
    cursor: "pointer",
  }),
};